// Quick View drawer.
//
// Opened by any [data-quick-view="<handle>"] button on a product card. Fetches
// the product's Quick View via Shopify's Section Rendering API
// (/products/{handle}?section_id=quick-view) and injects it into the drawer.
// The injected markup contains the <variant-selector> and <product-form> web
// components (loaded globally via core-assets), which upgrade on injection —
// so variant selection, price, and add-to-cart (opens the cart drawer) all work.

class QuickView {
  constructor(drawer) {
    this.drawer = drawer;
    this.overlay = drawer.querySelector("[data-quick-view-overlay]");
    this.panel = drawer.querySelector("[data-quick-view-panel]");
    this.content = drawer.querySelector("[data-quick-view-content]");
    this.loading = drawer.querySelector("[data-quick-view-loading]");
    this.cache = new Map();
    this.abortController = null;
    this.bind();
    this.watchCartDrawer();
  }

  // Close Quick View once the cart drawer opens (product-form opens it only after
  // a successful add — i.e. past the required-option check), so the cart drawer
  // replaces the Quick View instead of stacking behind it.
  watchCartDrawer() {
    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer) return;
    new MutationObserver(() => {
      if (
        cartDrawer.getAttribute("aria-hidden") === "false" &&
        !this.drawer.classList.contains("hidden")
      ) {
        this.close();
      }
    }).observe(cartDrawer, { attributes: true, attributeFilter: ["aria-hidden"] });
  }

  bind() {
    document.addEventListener("click", (event) => {
      const trigger = event.target.closest("[data-quick-view]");
      if (trigger) {
        event.preventDefault();
        this.open(trigger.dataset.quickView);
        return;
      }
      if (
        event.target.closest("[data-quick-view-close]") ||
        event.target.closest("[data-quick-view-overlay]")
      ) {
        this.close();
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !this.drawer.classList.contains("hidden")) this.close();
    });
  }

  open(handle) {
    if (!handle) return;
    this.drawer.classList.remove("hidden");
    document.body.classList.add("overflow-hidden");
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        this.overlay?.classList.replace("opacity-0", "opacity-100");
        this.panel?.classList.replace("translate-x-full", "translate-x-0");
      }),
    );
    this.load(handle);
  }

  async load(handle) {
    if (this.cache.has(handle)) {
      this.render(this.cache.get(handle));
      return;
    }
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    this.setLoading(true);

    try {
      const root = window.Shopify?.routes?.root || "/";
      const response = await fetch(`${root}products/${handle}?section_id=quick-view`, {
        signal: this.abortController.signal,
      });
      if (!response.ok) throw new Error(`Quick view ${handle} failed with ${response.status}`);
      const html = await response.text();
      this.cache.set(handle, html);
      this.render(html);
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Quick view load failed:", error);
      this.close();
    }
  }

  render(html) {
    // Injecting the section HTML upgrades its <variant-selector>/<product-form>.
    this.content.innerHTML = html;
    this.setLoading(false);
  }

  setLoading(isLoading) {
    this.loading?.classList.toggle("hidden", !isLoading);
    this.loading?.classList.toggle("flex", isLoading);
    this.content?.classList.toggle("hidden", isLoading);
  }

  close() {
    this.overlay?.classList.replace("opacity-100", "opacity-0");
    this.panel?.classList.replace("translate-x-0", "translate-x-full");
    setTimeout(() => {
      this.drawer.classList.add("hidden");
      document.body.classList.remove("overflow-hidden");
      // Clear so the injected web components disconnect (cache keeps the HTML).
      this.content.innerHTML = "";
    }, 800); // keep in sync with --drawer-duration in base.css
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const drawer = document.querySelector("[data-quick-view-drawer]");
  if (drawer) new QuickView(drawer);
});
